<template>
  <v-container fluid class="package-types-page">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-row align="center" justify="space-between">
              <v-col cols="auto">
                <span class="headline">Package Types</span>
                <v-chip class="ml-2" small color="primary" text-color="white">
                  {{ activePackages.length }} Active
                </v-chip>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  @click="showCreateDialog = true"
                >
                  <v-icon left>mdi-plus</v-icon>
                  New Package Type
                </v-btn>
                <v-btn
                  class="ml-2"
                  color="secondary"
                  :disabled="selectedPackages.length === 0"
                  @click="showBulkPrintDialog = true"
                >
                  <v-icon left>mdi-printer</v-icon>
                  Print QR Codes
                </v-btn>
                <v-btn
                  class="ml-2"
                  color="error"
                  outlined
                  :disabled="!canBulkDelete"
                  @click="confirmBulkDelete"
                >
                  <v-icon left>mdi-delete</v-icon>
                  {{ bulkDeleteButtonText }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text>
            <!-- Filters Row -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="searchQuery"
                  prepend-icon="mdi-magnify"
                  label="Search packages..."
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filterStatus"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filterType"
                  :items="typeOptions"
                  label="Type"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Analytics Summary -->
            <PackageAnalytics :analytics="analytics" @show-snackbar="showSnackbar" />

            <!-- Package Types Data Table -->
            <v-data-table
              v-model="selectedPackages"
              :headers="headers"
              :items="filteredPackages"
              :search="searchQuery"
              :loading="loading"
              item-key="packageTypeId"
              show-select
              class="elevation-1"
              :items-per-page="10"
            >
              <!-- Dimensions Column -->
              <template v-slot:item.dimensions="{ item }">
                {{ item.length }}" × {{ item.width }}" × {{ item.height }}"
              </template>

              <!-- Weight Column -->
              <template v-slot:item.weight="{ item }">
                {{ item.weight ? `${item.weight} lbs` : 'N/A' }}
              </template>

              <!-- Status Column -->
              <template v-slot:item.isActive="{ item }">
                <v-chip
                  :color="item.isActive ? 'success' : 'grey'"
                  small
                  dark
                >
                  {{ item.isActive ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>

              <!-- Usage Column -->
              <template v-slot:item.usageCount="{ item }">
                <v-chip
                  :color="getUsageColor(item.usageCount)"
                  small
                  text-color="white"
                >
                  {{ item.usageCount || 0 }} uses
                </v-chip>
              </template>

              <!-- Actions Column -->
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      v-on="on"
                      @click="viewQRCode(item)"
                    >
                      <v-icon small>mdi-qrcode</v-icon>
                    </v-btn>
                  </template>
                  <span>View QR Code</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      v-on="on"
                      @click="editPackage(item)"
                      :disabled="item.isSystemDefined"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.isSystemDefined ? 'Cannot edit system package' : 'Edit' }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-on="on"
                      @click="deletePackage(item)"
                      :disabled="item.isSystemDefined && !item.isCarrierPackage"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    {{ item.isSystemDefined && !item.isCarrierPackage ? 'Cannot delete system package' :
                       item.isCarrierPackage ? 'Hide this carrier package' : 'Delete' }}
                  </span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <PackageTypeDialog
      :value="showCreateDialog"
      :package-type="editingPackage"
      @input="showCreateDialog = $event"
      @saved="onPackageSaved"
      @show-snackbar="showSnackbar"
    />

    <!-- QR Code Preview Dialog -->
    <QRCodePreviewDialog
      :value="showQRDialog"
      :package-type="selectedPackageForQR"
      @input="showQRDialog = $event"
      @show-snackbar="showSnackbar"
    />

    <!-- Bulk Print Dialog -->
    <BulkPrintDialog
      :value="showBulkPrintDialog"
      :packages="selectedPackages"
      @input="showBulkPrintDialog = $event"
      @printed="onBulkPrinted"
      @show-snackbar="showSnackbar"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="showDeleteDialog"
      max-width="500"
      persistent
    >
      <v-card class="delete-dialog">
        <v-card-title class="delete-dialog-title">
          {{ deleteDialogTitle }}
        </v-card-title>
        <v-card-text class="delete-dialog-content">
          <p class="mb-2">{{ deleteDialogMessage }}</p>
          <div v-if="packagesToDelete.length > 1" class="mt-3">
            <strong>Packages to {{ hasCarrierPackagesInSelection ? 'hide/delete' : 'delete' }}:</strong>
            <ul class="mt-2 package-list">
              <li v-for="pkg in packagesToDelete" :key="pkg.packageTypeId">
                {{ pkg.name }}
                <v-chip x-small class="ml-1" v-if="pkg.isCarrierPackage" color="info" text-color="white">
                  carrier
                </v-chip>
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="cancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="executeDelete"
            :loading="deleting"
          >
            {{ deleteDialogConfirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <span>{{ snackbar.text }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue'
import PackageTypeDialog from '@/components/Settings/PackageTypes/PackageTypeDialog.vue'
import QRCodePreviewDialog from '@/components/Settings/PackageTypes/QRCodePreviewDialog.vue'
import BulkPrintDialog from '@/components/Settings/PackageTypes/BulkPrintDialog.vue'
import PackageAnalytics from '@/components/Settings/PackageTypes/PackageAnalytics.vue'
import { PackageTypeService } from '@/services/PackageTypeService'

export default defineComponent({
  name: 'PackageTypes',
  components: {
    PackageTypeDialog,
    QRCodePreviewDialog,
    BulkPrintDialog,
    PackageAnalytics
  },
  data() {
    return {
      loading: false,
      packages: [],
      selectedPackages: [],
      searchQuery: '',
      filterStatus: 'all',
      filterType: 'all',
      showCreateDialog: false,
      showQRDialog: false,
      showBulkPrintDialog: false,
      editingPackage: null,
      selectedPackageForQR: null,
      analytics: {},
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Dimensions', value: 'dimensions', sortable: false },
        { text: 'Weight', value: 'weight', sortable: true },
        { text: 'Status', value: 'isActive', sortable: true },
        { text: 'Usage', value: 'usageCount', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ],
      statusOptions: [
        { text: 'All', value: 'all' },
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' }
      ],
      typeOptions: [
        { text: 'All', value: 'all' },
        { text: 'System', value: 'system' },
        { text: 'Custom', value: 'custom' }
      ],
      packageService: null,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 3000
      },
      // Delete dialog state
      showDeleteDialog: false,
      packagesToDelete: [],
      deleting: false
    }
  },
  computed: {
    activePackages() {
      return this.packages.filter(p => p.isActive)
    },
    filteredPackages() {
      let result = this.packages

      // Apply status filter
      if (this.filterStatus !== 'all') {
        result = result.filter(p => 
          this.filterStatus === 'active' ? p.isActive : !p.isActive
        )
      }

      // Apply type filter
      if (this.filterType !== 'all') {
        result = result.filter(p => 
          this.filterType === 'system' ? p.isSystemDefined : !p.isSystemDefined
        )
      }

      return result
    },
    // Packages that can be deleted (excludes non-carrier system packages)
    deletableSelectedPackages() {
      return this.selectedPackages.filter(p => !p.isSystemDefined || p.isCarrierPackage)
    },
    canBulkDelete() {
      return this.deletableSelectedPackages.length > 0
    },
    bulkDeleteButtonText() {
      const count = this.deletableSelectedPackages.length
      if (count === 0) return 'Delete'
      return `Delete (${count})`
    },
    hasCarrierPackagesInSelection() {
      return this.packagesToDelete.some(p => p.isCarrierPackage)
    },
    deleteDialogTitle() {
      if (this.packagesToDelete.length === 1) {
        return this.packagesToDelete[0].isCarrierPackage ? 'Hide Carrier Package' : 'Delete Package Type'
      }
      return this.hasCarrierPackagesInSelection ? 'Hide/Delete Package Types' : 'Delete Package Types'
    },
    deleteDialogMessage() {
      if (this.packagesToDelete.length === 1) {
        const pkg = this.packagesToDelete[0]
        if (pkg.isCarrierPackage) {
          return `Are you sure you want to hide "${pkg.name}"? This carrier package will be deactivated and won't appear in package selections.`
        }
        return `Are you sure you want to delete "${pkg.name}"?`
      }
      const carrierCount = this.packagesToDelete.filter(p => p.isCarrierPackage).length
      const customCount = this.packagesToDelete.length - carrierCount

      if (carrierCount > 0 && customCount > 0) {
        return `You are about to hide ${carrierCount} carrier package(s) and delete ${customCount} custom package(s). This action cannot be undone for custom packages.`
      } else if (carrierCount > 0) {
        return `You are about to hide ${carrierCount} carrier package(s). They will be deactivated and won't appear in package selections.`
      }
      return `You are about to delete ${customCount} package type(s). This action cannot be undone.`
    },
    deleteDialogConfirmText() {
      if (this.packagesToDelete.length === 1) {
        return this.packagesToDelete[0].isCarrierPackage ? 'Hide Package' : 'Delete'
      }
      return this.hasCarrierPackagesInSelection ? 'Hide/Delete All' : 'Delete All'
    }
  },
  created() {
    this.packageService = new PackageTypeService()
  },
  mounted() {
    this.loadPackages()
    this.loadAnalytics()
  },
  methods: {
    async loadPackages() {
      this.loading = true
      try {
        this.packages = await this.packageService.getPackageTypes()
      } catch (error) {
        console.error('Failed to load package types:', error)
        this.showSnackbar({ 
          text: 'Failed to load package types', 
          color: 'error' 
        })
      } finally {
        this.loading = false
      }
    },
    async loadAnalytics() {
      try {
        this.analytics = await this.packageService.getAnalytics()
      } catch (error) {
        console.error('Failed to load analytics:', error)
      }
    },
    viewQRCode(packageType) {
      this.selectedPackageForQR = packageType
      this.showQRDialog = true
    },
    editPackage(packageType) {
      this.editingPackage = { ...packageType }
      this.showCreateDialog = true
    },
    deletePackage(packageType) {
      // Single package delete - show confirmation dialog
      this.packagesToDelete = [packageType]
      this.showDeleteDialog = true
    },
    confirmBulkDelete() {
      // Bulk delete - show confirmation dialog with deletable packages only
      this.packagesToDelete = [...this.deletableSelectedPackages]
      this.showDeleteDialog = true
    },
    cancelDelete() {
      this.showDeleteDialog = false
      this.packagesToDelete = []
    },
    async executeDelete() {
      if (this.packagesToDelete.length === 0) return

      this.deleting = true
      let successCount = 0
      let errorCount = 0

      try {
        for (const pkg of this.packagesToDelete) {
          try {
            await this.packageService.deletePackageType(pkg.packageTypeId)
            successCount++
          } catch (error) {
            console.error(`Failed to delete package type ${pkg.name}:`, error)
            errorCount++
          }
        }

        if (errorCount === 0) {
          const isCarrierOnly = this.packagesToDelete.every(p => p.isCarrierPackage)
          const message = this.packagesToDelete.length === 1
            ? (this.packagesToDelete[0].isCarrierPackage ? 'Carrier package hidden successfully' : 'Package type deleted successfully')
            : (isCarrierOnly ? `${successCount} carrier package(s) hidden successfully` : `${successCount} package type(s) deleted successfully`)
          this.showSnackbar({ text: message, color: 'success' })
        } else if (successCount > 0) {
          this.showSnackbar({
            text: `${successCount} succeeded, ${errorCount} failed`,
            color: 'warning'
          })
        } else {
          this.showSnackbar({
            text: 'Failed to delete package types',
            color: 'error'
          })
        }

        // Clear selection and refresh
        this.selectedPackages = []
        await this.loadPackages()
        await this.loadAnalytics()
      } finally {
        this.deleting = false
        this.showDeleteDialog = false
        this.packagesToDelete = []
      }
    },
    async onPackageSaved() {
      this.showCreateDialog = false
      this.editingPackage = null
      await this.loadPackages()
      await this.loadAnalytics()
    },
    onBulkPrinted() {
      this.showBulkPrintDialog = false
      this.selectedPackages = []
    },
    getUsageColor(count) {
      if (count > 100) return 'success'
      if (count > 50) return 'primary'
      if (count > 10) return 'warning'
      return 'grey'
    },
    showSnackbar(notification) {
      this.snackbar.text = notification.text || ''
      this.snackbar.color = notification.color || 'info'
      this.snackbar.timeout = notification.timeout || 3000
      this.snackbar.show = true
    }
  }
})
</script>

<style scoped>
.package-types-page {
  padding: 20px;
}

/* Improve readability with larger fonts */
.package-types-page >>> .v-data-table {
  font-size: 14px !important;
}

.package-types-page >>> .v-data-table .v-data-table__wrapper tbody tr td {
  font-size: 14px !important;
  padding: 12px 16px !important;
}

.package-types-page >>> .v-data-table .v-data-table__wrapper thead tr th {
  font-size: 15px !important;
  font-weight: 600 !important;
  padding: 12px 16px !important;
}

/* Improve chip readability */
.package-types-page >>> .v-chip {
  font-size: 13px !important;
  font-weight: 500 !important;
}

/* Improve button and icon sizes */
.package-types-page >>> .v-btn--icon.v-size--small {
  width: 36px !important;
  height: 36px !important;
}

.package-types-page >>> .v-btn--icon.v-size--small .v-icon {
  font-size: 18px !important;
}

/* Improve search and filter text */
.package-types-page >>> .v-text-field input,
.package-types-page >>> .v-select .v-select__selection {
  font-size: 14px !important;
}

/* Improve card title readability */
.package-types-page >>> .v-card-title .headline {
  font-size: 24px !important;
  font-weight: 500 !important;
}

/* Increase row height for better spacing */
.package-types-page >>> .v-data-table tbody tr {
  height: 52px !important;
}

</style>

<style>
/* Delete dialog styling - unscoped for v-dialog portal */
.delete-dialog .delete-dialog-title {
  font-size: 2rem !important;
  font-weight: 500;
}

.delete-dialog .delete-dialog-content {
  font-size: 1.6rem !important;
  line-height: 1.5;
}

.delete-dialog .delete-dialog-content p {
  font-size: 1.6rem !important;
}

.delete-dialog .delete-dialog-content strong {
  font-size: 1.6rem !important;
}

/* Delete dialog package list */
.delete-dialog .delete-dialog-content .package-list {
  max-height: 200px;
  overflow-y: auto;
  padding-left: 20px;
  margin-bottom: 0;
  font-size: 1.5rem;
}

.delete-dialog .delete-dialog-content .package-list li {
  padding: 4px 0;
}

/* Delete dialog buttons */
.delete-dialog .v-card__actions .v-btn {
  font-size: 1.4rem !important;
}
</style>